"use client";

import { PRICING_CONFIG } from "@routerbench/shared";

import { useCurrency } from "./use-currency";

interface ModelPriceStatsProps {
	inputPrices: Array<{ price: number; discount?: number }>;
	outputPrices: Array<{ price: number; discount?: number }>;
	/** Per-second USD prices (across resolution tiers) for video models. */
	videoPricesPerSecond?: number[];
	/** Flat USD price per generation for music models (one per provider). */
	musicPricesPerGeneration?: number[];
	/** Flat USD price per generated image (one per provider). */
	imagePricesPerImage?: number[];
	/** USD price per character for TTS models (one per provider). */
	ttsCharacterPrices?: number[];
	/** USD price per second for STT models (one per provider). */
	sttSecondPrices?: number[];
}

export function ModelPriceStats({
	inputPrices,
	outputPrices,
	videoPricesPerSecond,
	musicPricesPerGeneration,
	imagePricesPerImage,
	ttsCharacterPrices,
	sttSecondPrices,
}: ModelPriceStatsProps) {
	const [currency] = useCurrency();

	const sym = currency === "usd" ? "$" : "$";
	const rate = currency === "usd" ? 1 : PRICING_CONFIG.usdToInrRate;

	// Video models are metered per second of output, not per token.
	if (videoPricesPerSecond && videoPricesPerSecond.length > 0) {
		const lo = Math.min(...videoPricesPerSecond) * rate;
		const hi = Math.max(...videoPricesPerSecond) * rate;
		const label =
			lo === hi
				? `${sym}${lo.toFixed(2)}`
				: `${sym}${lo.toFixed(2)} – ${sym}${hi.toFixed(2)}`;
		return (
			<div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-black p-4 shadow-sm hover:shadow-md hover:border-gray-300 dark:hover:border-gray-700 transition-all sm:col-span-2">
				<div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
					Video Price
				</div>
				<div className="text-2xl font-bold text-gray-900 dark:text-white">
					{label}
				</div>
				<div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
					per second (by resolution)
				</div>
			</div>
		);
	}

	// Music models are flat-priced per generation, regardless of song length.
	if (musicPricesPerGeneration && musicPricesPerGeneration.length > 0) {
		const lo = Math.min(...musicPricesPerGeneration) * rate;
		const hi = Math.max(...musicPricesPerGeneration) * rate;
		const label =
			lo === hi
				? `${sym}${lo.toFixed(2)}`
				: `${sym}${lo.toFixed(2)} – ${sym}${hi.toFixed(2)}`;
		return (
			<div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-black p-4 shadow-sm hover:shadow-md hover:border-gray-300 dark:hover:border-gray-700 transition-all sm:col-span-2">
				<div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
					Music Price
				</div>
				<div className="text-2xl font-bold text-gray-900 dark:text-white">
					{label}
				</div>
				<div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
					per song (flat)
				</div>
			</div>
		);
	}

	// TTS models are priced per character (shown per 1M characters).
	if (ttsCharacterPrices && ttsCharacterPrices.length > 0) {
		const lo = Math.min(...ttsCharacterPrices) * 1e6 * rate;
		const hi = Math.max(...ttsCharacterPrices) * 1e6 * rate;
		const label =
			lo === hi
				? `${sym}${lo.toFixed(2)}`
				: `${sym}${lo.toFixed(2)} – ${sym}${hi.toFixed(2)}`;
		return (
			<div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-black p-4 shadow-sm hover:shadow-md hover:border-gray-300 dark:hover:border-gray-700 transition-all sm:col-span-2">
				<div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
					Speech Price
				</div>
				<div className="text-2xl font-bold text-gray-900 dark:text-white">
					{label}
				</div>
				<div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
					per million characters
				</div>
			</div>
		);
	}

	// STT models are priced per second of audio (shown per minute).
	if (sttSecondPrices && sttSecondPrices.length > 0) {
		const lo = Math.min(...sttSecondPrices) * 60 * rate;
		const hi = Math.max(...sttSecondPrices) * 60 * rate;
		const label =
			lo === hi
				? `${sym}${lo.toFixed(3)}`
				: `${sym}${lo.toFixed(3)} – ${sym}${hi.toFixed(3)}`;
		return (
			<div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-black p-4 shadow-sm hover:shadow-md hover:border-gray-300 dark:hover:border-gray-700 transition-all sm:col-span-2">
				<div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
					Transcription Price
				</div>
				<div className="text-2xl font-bold text-gray-900 dark:text-white">
					{label}
				</div>
				<div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
					per minute of audio
				</div>
			</div>
		);
	}

	// Image models are flat-priced per generated image, not per token.
	if (imagePricesPerImage && imagePricesPerImage.length > 0) {
		const lo = Math.min(...imagePricesPerImage) * rate;
		const hi = Math.max(...imagePricesPerImage) * rate;
		const label =
			lo === hi
				? `${sym}${lo.toFixed(3)}`
				: `${sym}${lo.toFixed(3)} – ${sym}${hi.toFixed(3)}`;
		return (
			<div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-black p-4 shadow-sm hover:shadow-md hover:border-gray-300 dark:hover:border-gray-700 transition-all sm:col-span-2">
				<div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
					Image Price
				</div>
				<div className="text-2xl font-bold text-gray-900 dark:text-white">
					{label}
				</div>
				<div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
					per image (flat)
				</div>
			</div>
		);
	}

	const getMinPrice = (prices: Array<{ price: number; discount?: number }>) => {
		if (prices.length === 0) {
			return null;
		}
		const values = prices.map(
			(p) => p.price * 1e6 * rate * (p.discount ? 1 - p.discount : 1),
		);
		return Math.min(...values);
	};

	const minInput = getMinPrice(inputPrices);
	const minOutput = getMinPrice(outputPrices);

	return (
		<>
			<div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-black p-4 shadow-sm hover:shadow-md hover:border-gray-300 dark:hover:border-gray-700 transition-all">
				<div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
					Input Price
				</div>
				<div className="text-2xl font-bold text-gray-900 dark:text-white">
					{minInput !== null ? `${sym}${minInput.toFixed(2)}` : "Free"}
				</div>
				<div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
					per million tokens
				</div>
			</div>
			<div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-black p-4 shadow-sm hover:shadow-md hover:border-gray-300 dark:hover:border-gray-700 transition-all">
				<div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
					Output Price
				</div>
				<div className="text-2xl font-bold text-gray-900 dark:text-white">
					{minOutput !== null ? `${sym}${minOutput.toFixed(2)}` : "Free"}
				</div>
				<div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
					per million tokens
				</div>
			</div>
		</>
	);
}
